import { useState, useCallback, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useGoogleDriveAccounts,
  useDisconnectDrive,
  useDriveFiles,
  importDriveFiles,
  type ImportProgress,
  type ImportResult,
} from '@/api/hooks/useGoogleDrive'
import { useMediaFolders, useCreateFolder } from '@/api/hooks/useMediaLibrary'
import { useAuth } from '@/api/hooks/useAuth'
import { useLanguage } from '@/i18n/LanguageContext'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  HardDrive,
  FolderOpen,
  Image,
  Video,
  ChevronRight,
  ArrowLeft,
  Download,
  Loader2,
  LogOut,
  FileQuestion,
  CheckSquare,
  Square,
  Plus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { GoogleDriveFile, MediaFolder } from '@/types'

function formatBytes(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function FileIcon({ file }: { file: GoogleDriveFile }) {
  if (file.isFolder) return <FolderOpen className="h-5 w-5 text-yellow-500" />
  if (file.mimeType.startsWith('image/')) return <Image className="h-5 w-5 text-blue-500" />
  if (file.mimeType.startsWith('video/')) return <Video className="h-5 w-5 text-purple-500" />
  return <FileQuestion className="h-5 w-5 text-muted-foreground" />
}

export function GoogleDriveTab() {
  const { t } = useLanguage()
  const { data: user } = useAuth()
  const queryClient = useQueryClient()
  const { data: accounts, isLoading: loadingAccounts } = useGoogleDriveAccounts()
  const disconnectDrive = useDisconnectDrive()
  const { data: folders } = useMediaFolders()
  const createFolder = useCreateFolder()

  // Selected account
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null)
  const activeAccount = accounts?.find((a) => a.id === activeAccountId) || accounts?.[0] || null

  // Navigation stack
  const [folderStack, setFolderStack] = useState<Array<{ id: string; name: string }>>([])
  const currentFolderId = folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null

  // Files
  const { data: filesData, isLoading: loadingFiles } = useDriveFiles(
    activeAccount?.id || null,
    currentFolderId,
  )

  // Selection
  const [selected, setSelected] = useState<Map<string, GoogleDriveFile>>(new Map())

  // Import
  const [importing, setImporting] = useState(false)
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null)
  const [targetFolderId, setTargetFolderId] = useState<string>('')
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')

  // Auto-select first account
  useEffect(() => {
    if (accounts?.length && !activeAccountId) {
      setActiveAccountId(accounts[0].id)
    }
  }, [accounts, activeAccountId])

  // Library folders (exclude cuts)
  const libraryFolders = folders?.filter((f) => f.type !== 'cuts') || []

  const navigateToFolder = useCallback((file: GoogleDriveFile) => {
    setFolderStack((prev) => [...prev, { id: file.id, name: file.name }])
    setSelected(new Map())
  }, [])

  const navigateBack = useCallback(() => {
    setFolderStack((prev) => prev.slice(0, -1))
    setSelected(new Map())
  }, [])

  const navigateToBreadcrumb = useCallback((index: number) => {
    setFolderStack((prev) => prev.slice(0, index))
    setSelected(new Map())
  }, [])

  const toggleSelect = useCallback((file: GoogleDriveFile) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(file.id)) {
        next.delete(file.id)
      } else {
        next.set(file.id, file)
      }
      return next
    })
  }, [])

  const selectAllMedia = useCallback(() => {
    if (!filesData?.files) return
    const mediaFiles = filesData.files.filter((f) => f.isMedia)
    const allSelected = mediaFiles.every((f) => selected.has(f.id))
    if (allSelected) {
      setSelected(new Map())
    } else {
      const next = new Map(selected)
      mediaFiles.forEach((f) => next.set(f.id, f))
      setSelected(next)
    }
  }, [filesData, selected])

  const handleConnect = () => {
    const userId = user?.id
    window.location.href = `/oauth/google-drive/redirect?userId=${userId}`
  }

  const handleDisconnect = (id: string) => {
    if (!confirm(t('Desconectar esta conta do Google Drive?'))) return
    disconnectDrive.mutate(id)
  }

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return
    createFolder.mutate(
      { name: newFolderName.trim() },
      {
        onSuccess: (data: { folder?: MediaFolder }) => {
          if (data?.folder?.id) setTargetFolderId(data.folder.id)
          setNewFolderName('')
          setShowNewFolder(false)
          toast.success(t('Pasta criada!'))
        },
      },
    )
  }

  const handleImport = () => {
    if (!activeAccount || selected.size === 0 || !targetFolderId) return

    const fileIds = Array.from(selected.values()).map((f) => f.id)
    const fileNames = Array.from(selected.values()).map((f) => f.name)
    const fileMimeTypes = Array.from(selected.values()).map((f) => f.mimeType)

    setImporting(true)
    setImportProgress(null)

    importDriveFiles(
      activeAccount.id,
      fileIds,
      fileNames,
      fileMimeTypes,
      targetFolderId,
      (progress) => setImportProgress(progress),
      (result) => {
        setImporting(false)
        setImportProgress(null)
        setSelected(new Map())
        queryClient.invalidateQueries({ queryKey: ['media-folders'] })
        toast.success(
          `${result.imported} ${t('arquivo(s) importado(s)')}${result.failed ? ` (${result.failed} ${t('falharam')})` : ''}`,
        )
      },
      (error) => {
        setImporting(false)
        setImportProgress(null)
        toast.error(error)
      },
    )
  }

  // ─── Not connected ───
  if (!loadingAccounts && (!accounts || accounts.length === 0)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
        <HardDrive className="h-16 w-16 text-muted-foreground/30" />
        <div className="text-center">
          <h3 className="text-lg font-medium">{t('Google Drive')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('Conecte sua conta do Google Drive para importar midias para a biblioteca')}
          </p>
        </div>
        <Button onClick={handleConnect} className="gap-2">
          <HardDrive className="h-4 w-4" />
          {t('Conectar Google Drive')}
        </Button>
      </div>
    )
  }

  if (loadingAccounts) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const mediaFiles = filesData?.files?.filter((f) => f.isMedia) || []
  const allMediaSelected = mediaFiles.length > 0 && mediaFiles.every((f) => selected.has(f.id))

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header: account info + actions */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-2">
          {activeAccount?.avatarUrl && (
            <img
              src={activeAccount.avatarUrl}
              alt=""
              className="h-6 w-6 rounded-full"
            />
          )}
          <span className="text-sm font-medium">{activeAccount?.googleEmail}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleConnect} className="gap-1 text-xs">
            <Plus className="h-3 w-3" />
            {t('Outra conta')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => activeAccount && handleDisconnect(activeAccount.id)}
            className="gap-1 text-xs text-destructive hover:text-destructive"
          >
            <LogOut className="h-3 w-3" />
            {t('Desconectar')}
          </Button>
        </div>
      </div>

      {/* Multiple accounts selector */}
      {accounts && accounts.length > 1 && (
        <div className="flex gap-1 border-b border-border px-4 py-1.5">
          {accounts.map((a) => (
            <button
              key={a.id}
              onClick={() => {
                setActiveAccountId(a.id)
                setFolderStack([])
                setSelected(new Map())
              }}
              className={cn(
                'rounded px-2 py-0.5 text-xs transition-colors',
                a.id === activeAccount?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80',
              )}
            >
              {a.googleEmail}
            </button>
          ))}
        </div>
      )}

      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 border-b border-border px-4 py-1.5 text-xs text-muted-foreground">
        {folderStack.length > 0 && (
          <button onClick={navigateBack} className="mr-1 hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          onClick={() => navigateToBreadcrumb(0)}
          className="hover:text-foreground"
        >
          {t('Meu Drive')}
        </button>
        {folderStack.map((f, i) => (
          <span key={f.id} className="flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            <button
              onClick={() => navigateToBreadcrumb(i + 1)}
              className="hover:text-foreground"
            >
              {f.name}
            </button>
          </span>
        ))}
      </div>

      {/* Select all + count */}
      <div className="flex items-center justify-between border-b border-border px-4 py-1.5">
        <div className="flex items-center gap-2">
          {mediaFiles.length > 0 && (
            <button
              onClick={selectAllMedia}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              {allMediaSelected ? (
                <CheckSquare className="h-3.5 w-3.5 text-primary" />
              ) : (
                <Square className="h-3.5 w-3.5" />
              )}
              {t('Selecionar tudo')} ({mediaFiles.length})
            </button>
          )}
          {selected.size > 0 && (
            <span className="text-xs font-medium text-primary">
              {selected.size} {t('selecionado(s)')}
            </span>
          )}
        </div>
      </div>

      {/* File list */}
      <div className="flex-1 overflow-auto">
        {loadingFiles ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded" />
            ))}
          </div>
        ) : !filesData?.files?.length ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
            <FolderOpen className="h-10 w-10 opacity-30" />
            <span className="text-sm">{t('Pasta vazia')}</span>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filesData.files.map((file) => {
              const isSelected = selected.has(file.id)
              return (
                <div
                  key={file.id}
                  className={cn(
                    'flex items-center gap-3 px-4 py-2 transition-colors',
                    file.isFolder
                      ? 'cursor-pointer hover:bg-muted/50'
                      : file.isMedia
                        ? 'cursor-pointer hover:bg-muted/50'
                        : 'opacity-50',
                    isSelected && 'bg-primary/5',
                  )}
                  onClick={() => {
                    if (file.isFolder) navigateToFolder(file)
                    else if (file.isMedia) toggleSelect(file)
                  }}
                >
                  {/* Checkbox for media files */}
                  <div className="w-5 shrink-0">
                    {file.isMedia && (
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(file)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>

                  {/* Thumbnail or icon */}
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded bg-muted">
                    {file.thumbnailLink && file.isMedia ? (
                      <img
                        src={file.thumbnailLink}
                        alt=""
                        className="h-full w-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <FileIcon file={file} />
                    )}
                  </div>

                  {/* Name + meta */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{file.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {file.isFolder
                        ? t('Pasta')
                        : `${file.mimeType.split('/')[1]?.toUpperCase() || ''} ${formatBytes(file.size)}`}
                    </p>
                  </div>

                  {/* Folder arrow */}
                  {file.isFolder && (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Import bar (sticky bottom) */}
      {selected.size > 0 && (
        <div className="border-t border-border bg-background px-4 py-3">
          {importing ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('Importando...')} {importProgress?.current || 0}/{importProgress?.total || selected.size}
                </span>
                <span>
                  {importProgress?.filename}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{
                    width: `${importProgress ? (importProgress.current / importProgress.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {/* Target folder selector */}
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span className="shrink-0 text-xs text-muted-foreground">{t('Pasta destino')}:</span>
                {showNewFolder ? (
                  <div className="flex items-center gap-1">
                    <input
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                      placeholder={t('Nome da pasta')}
                      className="h-7 w-40 rounded border border-input bg-background px-2 text-xs"
                      autoFocus
                    />
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCreateFolder}>
                      OK
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      onClick={() => setShowNewFolder(false)}
                    >
                      X
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Select value={targetFolderId} onValueChange={setTargetFolderId}>
                      <SelectTrigger className="h-7 w-48 text-xs">
                        <SelectValue placeholder={t('Escolher pasta')} />
                      </SelectTrigger>
                      <SelectContent>
                        {libraryFolders.map((f) => (
                          <SelectItem key={f.id} value={f.id} className="text-xs">
                            {f.parentId ? '  ' : ''}{f.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-1.5"
                      onClick={() => setShowNewFolder(true)}
                      title={t('Criar pasta')}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Import button */}
              <Button
                size="sm"
                onClick={handleImport}
                disabled={!targetFolderId || selected.size === 0}
                className="shrink-0 gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                {t('Importar')} {selected.size} {t('arquivo(s)')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
